/**
 * Input/output contracts for the eventLocation bulk callables.
 *
 * `bulkCreateEventLocations` accepts a pre-validated row set from the
 * admin's Excel import dialog (see `apps/admin/src/utils/imports/`).
 * Coordinates can't be set meaningfully from a spreadsheet , the import
 * creates `verified: false` / `coordinates: null` rows and the organizer
 * uses the admin's MapKit picker afterwards to pin coordinates without
 * re-typing the address.
 *
 * The earlier resolve-and-propose architecture (per ADR-126) routed every
 * imported row through the Apple Maps Server API for canonicalization.
 * That added quota concerns, a stub-vs-real fidelity gap in dev, and a
 * silent shape-mismatch class of bugs, all to solve the niche case of
 * "two docs differing only by punctuation." The bulk import now writes
 * the typed values verbatim with `verified: false`; organizers upgrade
 * locations to verified by opening them in the LocationPicker post-import.
 * Apple Maps Server API is not in this path.
 */

import { z } from 'zod';

export const bulkCreateEventLocationsRowSchema = z
  .object({
    label: z.string().min(1).max(200),
    color: z.string().min(1).max(20),
    address: z.string().nullable(),
    city: z.string().nullable(),
    stateOrRegion: z.string().nullable(),
    country: z.string().nullable(),
  })
  .strict();

export type BulkCreateEventLocationsRow = z.infer<
  typeof bulkCreateEventLocationsRowSchema
>;

export const bulkCreateEventLocationsInputSchema = z
  .object({
    eventId: z.string().min(1),
    /** Pre-validated rows from the admin import dialog. */
    rows: z.array(bulkCreateEventLocationsRowSchema).min(1).max(500),
  })
  .strict();

export type BulkCreateEventLocationsInput = z.infer<
  typeof bulkCreateEventLocationsInputSchema
>;

export type BulkCreateEventLocationsRowStatus =
  | 'created'
  | 'duplicate_label' // an existing eventLocation has the same label in this event
  | 'failed';

export interface BulkCreateEventLocationsRowResult {
  /** Index into the input `rows` array. */
  index: number;
  /** Label as written to Firestore. Same as the typed label; no canonicalization happens at import time. */
  label: string;
  /** Original typed label from the workbook row. Kept for symmetry with the resolve callable's payload. */
  typedLabel: string;
  status: BulkCreateEventLocationsRowStatus;
  /** New eventLocation document ID, populated when status === 'created'. */
  locationId?: string;
  /** Populated for any non-created status. */
  message?: string;
}

export interface BulkCreateEventLocationsResponse {
  success: true;
  results: BulkCreateEventLocationsRowResult[];
  summary: {
    created: number;
    duplicateLabel: number;
    failed: number;
  };
}

// ---------------------------------------------------------------------------
// bulkResolveEventLocations , preview pass for the admin import dialog.
//
// Same input as bulkCreate (eventId + rows). Does NOT write. Reports per
// row whether the row would be created fresh or matches an eventLocation
// that already exists in this event (case-insensitive label match). The
// dialog uses this to show the organizer what will land before committing.
// ---------------------------------------------------------------------------

export const bulkResolveEventLocationsInputSchema =
  bulkCreateEventLocationsInputSchema;

export type BulkResolveEventLocationsInput = z.infer<
  typeof bulkResolveEventLocationsInputSchema
>;

export type BulkResolveEventLocationsRowStatus =
  | 'new' // no existing eventLocation with this label; ready to create
  | 'matches_existing' // an existing eventLocation in this event already carries this label
  | 'failed'; // unexpected per-row server error

export interface BulkResolveEventLocationsProposal {
  /** Where the canonical fields came from. */
  source: 'typed' | 'existing';
  /**
   * When source='existing': the doc ID of the matching eventLocation. UI
   * shows "this row maps to an existing location named X" so the organizer
   * can either skip (keep existing) or override.
   */
  matchedExistingLocationId?: string;
  /** The label this row will land at , typed label, or the existing match's label. */
  canonicalLabel: string;
}

export interface BulkResolveEventLocationsRowResult {
  index: number;
  /** Original typed label from the workbook row. Always present. */
  typedLabel: string;
  status: BulkResolveEventLocationsRowStatus;
  /** Populated when status === 'new' or 'matches_existing'. */
  proposal?: BulkResolveEventLocationsProposal;
  /** Populated when status === 'failed'. */
  message?: string;
}

export interface BulkResolveEventLocationsResponse {
  success: true;
  results: BulkResolveEventLocationsRowResult[];
  summary: {
    new: number;
    matchesExisting: number;
    failed: number;
  };
}
